import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { RFI_SEND_REMINDER_TRIGGER } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import {
  getFormattedDate,
  getLatestDate,
  wasDateWithinLastDay,
} from 'dateUtils';
import { getHasElementsInArray } from 'utils';
import ResolvePopup from 'components/ResolvePopup';
import ReminderStatusText from 'router/subrouters/DataRoom/subrouters/RFI/pages/RfiList/components/RfiRequestsTable/components/ReminderStatusText';

import './RfiRecipientListItem.styles.scss';

type RfiRecipientListItemProps = {
  className?: string;
  dataRoomRfiId: string;
  email: string;
  reminders: Date[];
  isClosed: boolean;
  onReminderSent: (email: string) => void;
};

const RfiRecipientListItem: React.FC<RfiRecipientListItemProps> = ({
  className,
  dataRoomRfiId,
  email,
  reminders,
  isClosed,
  onReminderSent,
}) => {
  const { t } = useTranslation();

  const classes = classNames('rfi-recipient-list-item', className);

  const [onSendReminder, isLoading] = useCallbackAsync(async () => {
    await dataRoomApi.rfi.sendReminders(
      dataRoomRfiId,
      RFI_SEND_REMINDER_TRIGGER.SETTINGS_DRAWER,
      email,
    );

    onReminderSent(email);
  });

  const lastReminderDate = getLatestDate(reminders);

  const isRemindedRecently =
    getHasElementsInArray(reminders) && wasDateWithinLastDay(lastReminderDate);

  const remindButton = useMemo(
    () => (
      <Button
        variant="link"
        disabled={isLoading || isRemindedRecently}
        onClick={onSendReminder}
      >
        {t('remind')}
      </Button>
    ),
    [isLoading, isRemindedRecently, onSendReminder, t],
  );

  return (
    <li className={classes}>
      <div>
        <p className="rfi-recipient-list-item__email">{email}</p>
        <ReminderStatusText reminderDates={reminders} />
      </div>

      {!isClosed &&
        (isRemindedRecently ? (
          <ResolvePopup
            onHover
            popupContent={t('remindOnceEvery24Hours', {
              date: getFormattedDate(new Date(lastReminderDate)),
            })}
          >
            {remindButton}
          </ResolvePopup>
        ) : (
          remindButton
        ))}
    </li>
  );
};

export default RfiRecipientListItem;
